import { assetUrl } from "@/i18n/assets";
import { useState } from "react";
import { ScheduleServiceDialog } from "@/components/site/ScheduleServiceDialog";
import { Link } from "@/i18n/Link";
import { useCopy } from "@/i18n/LocaleContext";
import {
  ArrowRight,
  Award,
  Calendar,
  CheckCircle2,
  ClipboardList,
  MapPin,
  Package,
  Phone,
  ShieldCheck,
  Truck,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, organizationJsonLd, websiteJsonLd } from "@/components/site/Seo";
import { TrailerSilhouettes } from "@/components/site/TrailerSilhouettes";
import {
  ApplicationIcon,
  HubsIllustration,
  JacksCouplersIllustration,
  SuspensionIllustration,
  TiresWheelsIllustration,
} from "@/components/site/Illustrations";
import { getPosts, postImage } from "@/blog/posts";
import { useLocale } from "@/i18n/LocaleContext";
import { CATEGORY_LABELS, formatPostDate } from "@/i18n/blog-format";
import { postPrefetchHandlers, useIdlePrefetchPosts } from "@/blog/use-prefetch";
import { SITE_PHONE_DISPLAY } from "@/routes-data";

const COPY = {
  en: {
    heroEyebrow: "Authorized Dexter Distributor · Elkhart, IN",
    heroTitle1: "Trailer Care ",
    heroTitle2: "Experts.",
    whyTitle: "Elkhart, Indiana. 30+ years trusted.",
    whyItems: [
      { title: "Trailers Only expertise", copy: "Keep your trailer moving with dependable parts, expert service, and a free quote tailored to your needs." },
      { title: "Dexter Certified", copy: "Authorized Dexter distributor bringing you premium axles and trailers. Industry-leading quality you can trust, backed by decades of excellence." },
      { title: "Industry roots", copy: "Located in the RV and trailer capital of the world. Fast parts availability and quick service that competitors can't match." },
      { title: "Parts Hub", copy: "Complete trailer solutions from axles to grease caps. One trusted source for everything you need - no more shopping around." },
    ],
    shopCta: "Shop Parts Online",
    heroSub: "Trailer axles, suspension, brakes, and parts - backed by a service team with 60+ years of combined experience. We get Elkhart back on the road.",
    scheduleBtn: "Schedule Service",
    trustStrip: { loc: "Elkhart, IN", exp: "60+ Years Combined Experience" },
    stats: [
      { val: "60+", label: "Years combined experience" },
      { val: "1k–10k", label: "lb axle capacities" },
      { val: "4", label: "Product categories in stock" },
      { val: "1", label: "Authorized Dexter distributor" },
    ],
    trustBand: [
      { label: "Authorized Dexter Distributor" },
      { label: "60+ Years Combined Experience" },
      { label: "OEM-Quality Parts" },
      { label: "Local. Elkhart, IN." },
    ],
    prodTitle: "Every part of the trailer, in stock.",
    prodSub: "We carry the full Dexter Group line plus genuine replacement parts for nearly every trailer on the road today. Browse by category - or call and we'll spec it for you.",
    prodAll: "View all products",
    prodTiles: [
      { title: "Hubs, Drums & Brakes", desc: "1,000–10,000 lb capacities. Electric, hydraulic drum, and hydraulic disc." },
      { title: "Suspension Parts", desc: "Springs, hangers, equalizers, and hardware - double-eye and slipper systems." },
      { title: "Jacks & Couplers", desc: "800–10,000 lb jacks, A-frame couplers, surge actuators, and accessories." },
      { title: "Tires & Wheels", desc: '8"–17.5" trailer tires and wheels. Steel, aluminum, chrome, and galvanized.' },
    ],
    appTitle: "Whether you have one - or a fleet.",
    appSub: "Whatever you tow, we've got the parts and the know-how. Click any application to find the components and capacities that fit.",
    apps: [
      { title: "Utility", desc: "Single & tandem utility trailers - landscaping, hauling, deliveries." },
      { title: "Cargo & Enclosed", desc: "Enclosed cargo, motorcycle, and toy haulers - 3.5k to 10k lb." },
      { title: "Boat", desc: "Bunk and roller trailers - bearings, brakes, and corrosion-rated parts." },
      { title: "RV & Travel", desc: "Travel trailers and 5th-wheels - Dexter Tor-Flex and leaf-spring axles." },
      { title: "Light Commercial", desc: "Landscape, contractor, and small equipment trailers - up to 10k lb axles." },
      { title: "Ag & Utility", desc: "Farm wagons, gravity boxes, and utility implements - built to take a beating." },
    ],
    processTitle: "Straightforward service. No runaround.",
    processSubPart1: "Bring the trailer to our Elkhart shop - ",
    processSubLink: "most jobs are done the same day",
    processSubPart2: " you drop off.",
    process: [
      { title: "Call or stop in", desc: "Tell us what you're hauling and what's wrong. We'll spec the right part - first time." },
      { title: "We service or supply", desc: "Bring it to the Elkhart shop, or pick up the part. Most repairs scheduled within the week." },
      { title: "Back on the road", desc: "Every job is finished by people whose name is on the door. That's the Axle Inc. promise." },
    ],
    dexterBand: {
      eyebrow: "Authorized Distributor",
      title: "The local face of Dexter Group.",
      desc: "Axle Inc. is the Elkhart-area's exclusive Dexter Group distributor. That means full access to Dexter's complete catalog of axles, brakes, suspension, hubs and drums, and ag/utility components - backed by local stock, local service, and local people.",
      btn1: "About our Dexter partnership",
      btn2: "Current rebates",
      features: ["Axles 1k–10k lb", "Electric & hydraulic brakes", "Suspension systems", "Hubs, drums, rotors", "Ag & utility components", "Replacement parts"]
    },
    blog: {
      eyebrow: "From the shop",
      title: "Practical guides & how-tos",
      btn: "All articles",
      readMore: "Read article →"
    },
    footerCta: {
      title: "Get the right part. Talk to a real person.",
      sub: "Call us or stop by the Elkhart shop.",
      btn2: "Visit the shop"
    },
    seoTitle: "Trailer Axles in Elkhart, IN | Dexter Distributor & Repair",
    seoDesc: "Trailer axles, parts, and on-site repair in Elkhart, IN. Authorized Dexter Group distributor serving Elkhart County, northern Indiana, and SW Michigan. Call (574) 264-9434."
  },
  es: {
    heroEyebrow: "Distribuidor Autorizado Dexter · Elkhart, IN",
    heroTitle1: "Expertos en ",
    heroTitle2: "Remolques.",
    whyTitle: "Elkhart, Indiana. 30+ años de confianza.",
    whyItems: [
      { title: "Expertos en remolques", copy: "Mantenga su remolque en movimiento con piezas confiables, servicio experto y una cotización gratuita adaptada a sus necesidades." },
      { title: "Certificados por Dexter", copy: "Distribuidor autorizado de Dexter que le ofrece ejes y remolques de primera calidad. Calidad líder en la industria en la que puede confiar, respaldada por décadas de excelencia." },
      { title: "Raíces en la industria", copy: "Ubicados en la capital mundial de los vehículos recreativos (RV) y remolques. Disponibilidad rápida de piezas y servicio veloz que la competencia no puede igualar." },
      { title: "Centro de piezas", copy: "Soluciones completas para remolques, desde ejes hasta tapas de grasa. Una fuente confiable para todo lo que necesita; ya no tendrá que buscar en otro lado." },
    ],
    shopCta: "Comprar piezas en línea",
    heroSub: "Ejes para remolque, suspensión, frenos y piezas, respaldados por un equipo de servicio con más de 60 años de experiencia combinada. Ayudamos a Elkhart a volver al camino.",
    scheduleBtn: "Programar servicio",
    trustStrip: { loc: "Elkhart, IN", exp: "Más de 60 años de experiencia combinada" },
    stats: [
      { val: "60+", label: "Años de experiencia combinada" },
      { val: "1k–10k", label: "lb de capacidad de eje" },
      { val: "4", label: "Categorías de productos en stock" },
      { val: "1", label: "Distribuidor autorizado Dexter" },
    ],
    trustBand: [
      { label: "Distribuidor Autorizado Dexter" },
      { label: "Más de 60 años de experiencia" },
      { label: "Piezas de calidad OEM" },
      { label: "Local. Elkhart, IN." },
    ],
    prodTitle: "Todas las piezas de remolque, en stock.",
    prodSub: "Contamos con toda la línea de Dexter Group además de piezas de repuesto originales para casi cualquier remolque en la carretera hoy en día. Navegue por categoría o llámenos y encontraremos la especificación correcta para usted.",
    prodAll: "Ver todos los productos",
    prodTiles: [
      { title: "Mazas, tambores y frenos", desc: "Capacidades de 1,000–10,000 lb. Eléctricos, de tambor hidráulico y de disco hidráulico." },
      { title: "Piezas de suspensión", desc: "Ballestas, soportes, ecualizadores y tornillería; sistemas de ojo doble y slipper." },
      { title: "Gatos y acopladores", desc: "Gatos de 800–10,000 lb, acopladores tipo A, actuadores de inercia y accesorios." },
      { title: "Llantas y ruedas", desc: 'Llantas y ruedas para remolque de 8"–17.5". Acero, aluminio, cromo y galvanizadas.' },
    ],
    appTitle: "Ya sea que tenga uno o una flota.",
    appSub: "Lo que sea que remolque, tenemos las piezas y los conocimientos. Haga clic en cualquier aplicación para encontrar los componentes y capacidades que encajan.",
    apps: [
      { title: "Utilitario", desc: "Remolques utilitarios simples y de tándem: paisajismo, carga, entregas." },
      { title: "Carga y cerrados", desc: "Carga cerrada, motocicletas y remolques de juguetes: de 3.5k a 10k lb." },
      { title: "Embarcaciones", desc: "Remolques tipo litera y de rodillos: rodamientos, frenos y piezas con protección contra corrosión." },
      { title: "RV y Viajes", desc: "Remolques de viaje y quinta rueda: ejes Dexter Tor-Flex y ballestas." },
      { title: "Comercial ligero", desc: "Paisajismo, contratistas y remolques de equipos pequeños: hasta ejes de 10k lb." },
      { title: "Agrícola y utilitario", desc: "Carros agrícolas, cajas de gravedad e implementos utilitarios: fabricados para soportar el trabajo duro." },
    ],
    processTitle: "Servicio directo. Sin rodeos.",
    processSubPart1: "Traiga el remolque a nuestro taller en Elkhart; ",
    processSubLink: "la mayoría de los trabajos se completan el mismo día",
    processSubPart2: " que lo entrega.",
    process: [
      { title: "Llame o visítenos", desc: "Díganos qué está remolcando y qué sucede. Especificaremos la pieza correcta, a la primera." },
      { title: "Servicio o suministro", desc: "Traiga su equipo al taller de Elkhart o pase a recoger la pieza. La mayoría de las reparaciones se programan dentro de la semana." },
      { title: "De vuelta en la carretera", desc: "Cada trabajo es terminado por personas cuyo nombre aparece en la puerta. Esa es la promesa de Axle Inc." },
    ],
    dexterBand: {
      eyebrow: "Distribuidor Autorizado",
      title: "La cara local de Dexter Group.",
      desc: "Axle Inc. es el distribuidor exclusivo de Dexter Group en el área de Elkhart. Eso significa acceso total al catálogo completo de Dexter de ejes, frenos, suspensión, mazas y tambores, y componentes agrícolas/utilitarios, respaldado por inventario local, servicio local y gente local.",
      btn1: "Sobre nuestra alianza con Dexter",
      btn2: "Reembolsos actuales",
      features: ["Ejes 1k–10k lb", "Frenos eléctricos e hidráulicos", "Sistemas de suspensión", "Mazas, tambores, rotores", "Componentes agrícolas y utilitarios", "Piezas de repuesto"]
    },
    blog: {
      eyebrow: "Desde el taller",
      title: "Guías prácticas y tutoriales",
      btn: "Todos los artículos",
      readMore: "Leer artículo →"
    },
    footerCta: {
      title: "Obtenga la pieza correcta. Hable con una persona real.",
      sub: "Llámenos o pase por el taller de Elkhart.",
      btn2: "Visitar el taller"
    },
    seoTitle: "Ejes para remolque en Elkhart, IN | Distribuidor y Reparación Dexter",
    seoDesc: "Ejes para remolque, piezas y reparación en sitio en Elkhart, IN. Distribuidor autorizado de Dexter Group que sirve al condado de Elkhart, el norte de Indiana y el suroeste de Michigan. Llame al (574) 264-9434."
  }
};

export default function Home() {
  const t = useCopy(COPY);
  const { locale } = useLocale();
  const recent = getPosts(locale).slice(0, 3);
  const [whyOpen, setWhyOpen] = useState<number | null>(null);
  useIdlePrefetchPosts(recent.map((p) => p.slug), 3, locale);
  return (
    <PageLayout>
      <Seo
        title={t.seoTitle}
        description={t.seoDesc}
        path="/"
        jsonLd={[organizationJsonLd(), websiteJsonLd()]}
      />

      <section className="relative bg-secondary text-secondary-foreground overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundImage:
              "radial-gradient(60% 80% at 85% 30%, rgba(255,40,0,0.18) 0%, transparent 65%), radial-gradient(50% 60% at 10% 90%, rgba(255,255,255,0.05) 0%, transparent 60%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" aria-hidden="true" />

        <div className="relative container mx-auto px-4 pt-14 pb-14 md:pt-20 md:pb-20">
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur px-3.5 py-1.5 text-[11px] md:text-xs uppercase tracking-[0.18em] font-semibold text-white mb-7">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              {t.heroEyebrow}
            </div>
          </div>

          <h1 className="text-center font-bold leading-[0.95] tracking-[-0.035em] whitespace-nowrap text-[clamp(2rem,7.6vw,8rem)]">
            <span className="text-white">{t.heroTitle1}</span>
            <span className="text-primary">{t.heroTitle2}</span>
          </h1>

          <div className="relative mt-10 md:mt-12 max-w-5xl mx-auto">
            <div className="absolute -inset-3 md:-inset-5 rounded-2xl bg-gradient-to-br from-primary/30 via-primary/5 to-transparent blur-2xl opacity-70 pointer-events-none" aria-hidden="true" />
            <div className="relative rounded-xl md:rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-2xl shadow-black/50 bg-primary text-white px-6 py-8 md:px-10 md:py-10">
              <h2 className="text-2xl md:text-4xl font-bold tracking-tight mb-7 md:mb-9">
                {t.whyTitle}
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8 items-start">
                {t.whyItems.map(({ title, copy }, i) => {
                  const icons = [Wrench, () => <img src={assetUrl("/brand/dexter-white.svg")} alt="" width={293} height={234} className="h-11 md:h-12 w-auto block" loading="lazy" decoding="async" aria-hidden="true" />, MapPin, Package];
                  const Icon = icons[i];
                  return (
                    <div key={title} className="group text-center">
                      <button
                        type="button"
                        className="block w-full text-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
                        aria-expanded={whyOpen === i}
                        aria-controls={`why-axle-copy-${i}`}
                        onClick={() => setWhyOpen(whyOpen === i ? null : i)}
                      >
                        <span className="h-12 flex items-end justify-center mb-3">
                          <Icon className={i !== 1 ? "h-9 w-9 md:h-10 md:w-10 text-white" : ""} aria-hidden="true" />
                        </span>
                        <span className="block text-base md:text-lg font-bold leading-snug">
                          {title}
                        </span>
                      </button>
                      <p
                        id={`why-axle-copy-${i}`}
                        className={`mt-2 text-sm text-white/90 leading-relaxed transition-all duration-300 ${
                          whyOpen === i
                            ? ""
                            : "lg:opacity-0 lg:max-h-0 lg:overflow-hidden lg:group-hover:opacity-100 lg:group-hover:max-h-52 lg:group-focus-within:opacity-100 lg:group-focus-within:max-h-52"
                        }`}
                      >
                        {copy}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-6 md:mt-8 max-w-5xl mx-auto">
            <Button
              asChild
              size="lg"
              className="w-full bg-white text-secondary hover:bg-white/90 font-extrabold text-base md:text-lg py-6 shadow-lg"
            >
              <a
                href="https://parts.axleinc.com/"
                target="_blank"
                rel="noopener"
              >
                {t.shopCta} <ArrowRight className="ml-1.5 h-5 w-5" />
              </a>
            </Button>
          </div>

          <div className="mt-12 md:mt-14 grid md:grid-cols-2 gap-8 md:gap-10 items-center max-w-5xl mx-auto">
            <p className="text-base md:text-lg text-secondary-foreground/80 leading-relaxed">
              {t.heroSub}
            </p>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <ScheduleServiceDialog>
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25 px-6">
                  <Calendar className="mr-1.5 h-4 w-4" /> {t.scheduleBtn}
                </Button>
              </ScheduleServiceDialog>
            </div>
          </div>

          <div className="mt-10 pt-7 border-t border-white/10 flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-sm text-secondary-foreground/70 max-w-5xl mx-auto">
            <span className="inline-flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" /> {t.trustStrip.loc}
            </span>
            <a
              href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}
              className="inline-flex items-center gap-2 hover:text-white transition-colors"
            >
              <Phone className="h-4 w-4 text-primary" /> {SITE_PHONE_DISPLAY}
            </a>
            <span className="inline-flex items-center gap-2">
              <Award className="h-4 w-4 text-primary" /> {t.trustStrip.exp}
            </span>
          </div>
        </div>

        <div className="relative border-t border-white/10 bg-black/20">
          <div className="container mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {t.stats.map((s) => (
              <div key={s.label} className="flex flex-col">
                <span className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                  {s.val}
                </span>
                <span className="text-xs uppercase tracking-[0.16em] text-white/55 mt-1">
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TrailerSilhouettes />

      <section className="bg-background border-b border-border">
        <div className="container mx-auto px-4 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { icon: Award, label: t.trustBand[0].label },
            { icon: Wrench, label: t.trustBand[1].label },
            { icon: ShieldCheck, label: t.trustBand[2].label },
            { icon: MapPin, label: t.trustBand[3].label },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex flex-col items-center gap-2.5">
              <Icon className="h-6 w-6 text-primary" />
              <span className="text-sm font-medium text-foreground/80 max-w-[14ch]">{label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="container mx-auto px-4 py-20 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div className="max-w-2xl">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-primary-strong font-semibold mb-3">
              Built to spec
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t.prodTitle}
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              {t.prodSub}
            </p>
          </div>
          <Button asChild variant="outline" className="self-start md:self-auto">
            <Link to="/products/">
              {t.prodAll} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {[
            { Art: HubsIllustration, path: "/products/hubs/" },
            { Art: SuspensionIllustration, path: "/products/suspension-parts/" },
            { Art: JacksCouplersIllustration, path: "/products/jacks-couplers/" },
            { Art: TiresWheelsIllustration, path: "/products/tires-wheels/" },
          ].map(({ Art, path }, i) => (
            <Link
              key={path}
              to={path}
              className={`group relative rounded-md overflow-hidden border border-border bg-card hover:border-primary hover:shadow-lg hover:-translate-y-0.5 transition-all ${
                i === 0 ? "lg:col-span-2 lg:row-span-1" : ""
              }`}
            >
              <Art />
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
                  {t.prodTiles[i].title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t.prodTiles[i].desc}</p>
                <span className="mt-4 inline-flex items-center text-sm font-medium text-primary-strong">
                  Explore <ArrowRight className="ml-1 h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 py-20 md:py-24">
          <div className="max-w-2xl mb-12">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-primary-strong font-semibold mb-3">
              Built for every trailer
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t.appTitle}
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              {t.appSub}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {["utility", "cargo", "boat", "rv", "commercial", "ag"].map((kind, i) => (
              <Link
                key={kind}
                to="/products/"
                className="group relative bg-background border border-border rounded-md p-6 hover:border-primary hover:shadow-md transition-all overflow-hidden"
              >
                <div
                  className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-primary/5 group-hover:bg-primary/10 transition-colors"
                  aria-hidden="true"
                />
                <ApplicationIcon
                  kind={kind}
                  className="relative h-8 w-16 text-secondary group-hover:text-primary transition-colors mb-4"
                />
                <h3 className="relative font-semibold mb-1">{t.apps[i].title}</h3>
                <p className="relative text-sm text-muted-foreground leading-relaxed">
                  {t.apps[i].desc}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-20 md:py-24">
        <div className="max-w-2xl mb-12">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-primary-strong font-semibold mb-3">
            How we work
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            {t.processTitle}
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            {t.processSubPart1}
            <Link to="/services/emergency-same-day-trailer-repair/" className="text-primary-strong hover:underline underline-offset-4">
              {t.processSubLink}
            </Link>
            {t.processSubPart2}
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[Phone, Wrench, Truck].map((Icon, i) => (
            <div
              key={i}
              className="relative bg-card border border-border rounded-md p-7 hover:border-primary/40 transition-colors"
            >
              <span
                aria-hidden="true"
                className="absolute top-5 right-6 inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-primary-deep px-2 text-xs font-bold text-white leading-none"
              >
                0{i + 1}
              </span>
              <Icon className="h-8 w-8 text-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t.process[i].title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{t.process[i].desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="relative bg-secondary text-secondary-foreground overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          aria-hidden="true"
          style={{
            backgroundImage:
              "radial-gradient(circle at 80% 30%, rgba(255,40,0,0.6) 0%, transparent 45%)",
          }}
        />
        <div className="relative container mx-auto px-4 py-16 md:py-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-white font-semibold mb-4">
              {t.dexterBand.eyebrow}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t.dexterBand.title}
            </h2>
            <p className="mt-5 text-secondary-foreground/80 leading-relaxed max-w-xl">
              {t.dexterBand.desc}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/dexter/">
                  {t.dexterBand.btn1} <ArrowRight className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="bg-transparent border-white/30 text-white hover:bg-white/10"
              >
                <Link to="/rebates/">
                  <ClipboardList className="mr-1.5 h-4 w-4" /> {t.dexterBand.btn2}
                </Link>
              </Button>
            </div>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-3">
            {t.dexterBand.features.map((label) => (
              <div
                key={label}
                className="flex items-start gap-2 border border-white/10 bg-white/[0.03] rounded-md px-4 py-3 text-sm"
              >
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-20 md:py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary-strong font-semibold mb-3">
              {t.blog.eyebrow}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t.blog.title}
            </h2>
          </div>
          <Button asChild variant="outline">
            <Link to="/blog/">{t.blog.btn}</Link>
          </Button>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {recent.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}/`}
              {...postPrefetchHandlers(p.slug, locale)}
              className="group block bg-card rounded-md overflow-hidden border border-border hover:border-primary hover:shadow-md transition-all"
            >
              <div className="relative h-48 bg-secondary overflow-hidden">
                <img
                  src={assetUrl(postImage(p.slug, locale))}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div
                  className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/40 pointer-events-none"
                  aria-hidden="true"
                />
                <div className="relative h-full flex flex-col justify-between p-5">
                  <span className="self-start text-[10px] uppercase tracking-[0.2em] font-semibold text-white bg-primary-deep rounded-full px-2.5 py-1 shadow-md">
                    {CATEGORY_LABELS[locale][p.category]}
                  </span>
                  <span className="text-white text-xs uppercase tracking-[0.18em] font-semibold drop-shadow group-hover:translate-x-1 transition-transform">
                    {t.blog.readMore}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <p className="text-xs text-muted-foreground mb-2">{formatPostDate(p, locale)}</p>
                <h3 className="font-semibold leading-snug mb-2 group-hover:text-primary transition-colors">
                  {p.title}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                  {p.excerpt}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-primary-deep text-primary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold">
              {t.footerCta.title}
            </h2>
            <p className="mt-2 text-primary-foreground">
              {t.footerCta.sub}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-white text-primary-strong hover:bg-white/90">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {SITE_PHONE_DISPLAY}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-transparent border-white/40 text-white hover:bg-white/10"
            >
              <Link to="/contact/">{t.footerCta.btn2}</Link>
            </Button>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
